// AppSidebar — 전 화면 공유 사이드바(앱 nav·현재앱 콘텐츠·사용량·계정·폭 드래그). 메뉴 기본값은 utils/sidebarMenu DEFAULT_NAV.
import { useState, useEffect, useLayoutEffect, useRef, useCallback, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { chatAPI, authAPI, centerCostAPI, sidebarMenuAPI } from '../api/client';
import { GlobalAnnouncementPopup } from './Announcements';
import { canSeeMenu } from '../utils/menuAccess';
import {
  DEFAULT_NAV, resolveNav, withCenterSites, withCenterGroupIfUnset, withClaimGroupIfUnset, withWorkGroupIfUnset,
  readCachedConfig, writeCachedConfig, SIDEBAR_MENU_EVENT,
} from '../utils/sidebarMenu';
import {
  readCachedFavorites, writeCachedFavorites, toggleFavorite, favoriteItems,
  beginFavoriteSave, endFavoriteSave, favoriteSeq, favoritesBusy, noteFavoriteChange,
  FAVORITES_CACHE_KEY, FAVORITES_EVENT, MAX_FAVORITES,
} from '../utils/sidebarFavorites';
import { syncFontFromUser } from '../utils/fontPrefs';
import { logoutGateway } from '../lib/gatewayLogout';
import { scaleOf } from '../lib/floatingBounds';
import { getUnread, subscribe as subscribeUnread, refreshUnread, startUnreadPolling, resetUnread } from '../collabUnreadStore';
import { fmtKstShort } from '../utils/kstTime';
import useIsMobile from '../hooks/useIsMobile';
import MobileTabBar from './MobileTabBar';
import MobileDrawer from './MobileDrawer';
import SidebarViewControls from './SidebarViewControls';
import NotificationCenter from './NotificationCenter';
import GlobalSearch from './GlobalSearch';
import { canOpenSplitApp, useSplitView } from './SplitShell';
import {
  IconHome, IconBook, IconMessageCircle, IconMicrophone, IconBooks, IconMessages,
  IconFolder, IconFileText, IconCalendar, IconMail, IconVideo,
  IconLayoutKanban, IconSitemap, IconHelp, IconSpeakerphone, IconTicket, IconSettings,
  IconFileInvoice, IconChevronLeft, IconChevronRight, IconChevronDown, IconUser,
  IconChartBar, IconWorld, IconBell, IconSearch, IconColumns2, IconSparkles,
  IconBuildingWarehouse, IconReportMoney, IconTrendingUp, IconBuilding,
  IconFileDownload, IconPresentation, IconClipboardList, IconBarcode, IconStar, IconCalculator, IconGauge,
  IconTruckLoading, IconMenu2, IconCode, IconAddressBook,
} from '@tabler/icons-react';
import { SERVICE_NAME } from '../utils/brand';
import { COLORS } from '../utils/uiStyles';

/* ── 메뉴 설정(확장 지점) ──
 *  기본 메뉴는 utils/sidebarMenu.js 의 DEFAULT_NAV 한 곳에 있다 — 앱/링크 추가는 그 배열에 한 줄.
 *  관리자 설정(app_settings 'sidebar_menu' · 순서·그룹·이름·숨김)은 resolveNav 가 그 위에 적용한다.
 *  ⚠️노출/접근 권한은 **utils/menuAccess.canSeeMenu(단일소스)** 로만 판정한다 — 숨김은 권한이 아니다.
 *  센터 메뉴도 같다(2026-10-23) — 고정 키 4개는 DEFAULT_NAV '센터' 그룹, 센터별(center:<token>)은 withCenterSites 가 끼운다.
 */
// 메뉴 key → 단색 라인 아이콘(Tabler 아웃라인). 색은 아래 ICON_TONES(toneFor) — 표에 없으면 글자색 상속(currentColor).
const ICONS = {
  home: IconHome, help: IconBook, my: IconUser, chat: IconSparkles, meetings: IconMicrophone,   // AI 채팅 = 반짝이(시안 F · f8b2)
  generated: IconFileDownload, documents: IconBooks, docauto: IconFileInvoice, messenger: IconMessages, drive: IconFolder, office: IconFileText,
  calendar: IconCalendar, mail: IconMail, mail2: IconMail, talk: IconVideo,
  collabtools: IconLayoutKanban, directory: IconSitemap, inquiries: IconHelp,
  apps: IconCode,   // AI 개발실 = 코드 </> (11-02 — 두 표 모두에서 빠져 이 줄만 아이콘이 없었다)
  dxboard: IconLayoutKanban, execmeeting: IconPresentation, supportboard: IconLayoutKanban, board: IconSpeakerphone, tickets: IconTicket, admin: IconSettings,
  taskpm: IconLayoutKanban,
  'abc-analysis': IconChartBar,
  contracts: IconFileInvoice,
  pdas: IconBarcode,
  'dispatch-calc': IconCalculator,
  'inbound-capa': IconTruckLoading,
  capa: IconGauge,
  devreq: IconClipboardList,
  // 업무 통합 — 고객사 기준 정보 및 이력관리(1단계).
  'work-customers': IconAddressBook,
  // 클레임 — 등록(2단계) · 보상기준 마스터 · 사유코드 관리.
  'claim-register': IconFileInvoice, 'claim-standards': IconReportMoney, 'claim-reasons': IconClipboardList,
  // 센터 메뉴 — 키가 고정인 넷(DEFAULT_NAV)만 여기 둔다.
  //   'center:<token>' 은 계정마다 토큰이 달라 맵(정확 일치)으로는 못 잡는다 → iconFor 의 접두어 폴백이 맡는다.
  'center-all': IconReportMoney, 'center-daily': IconTrendingUp, 'center-shippers': IconBuilding,
  'center-perf': IconChartBar,
};
// 🔴 아이콘 선택은 이 함수 한 곳으로만 한다(모바일 드로어·데스크톱 메뉴·하위 메뉴 전부).
//    2026-08-18 에 모바일에만 센터 폴백을 넣어 데스크톱은 접으면 빈 버튼만 남았다 — 자리를 늘리지 말 것(f8b2 로 메뉴를 그리는 곳이 aside 하나가 됐다).
const iconFor = (key) => ICONS[key]
  || (String(key).startsWith('center:') ? IconBuildingWarehouse : null);
// 메뉴 key → 아이콘 색(시안 F 색 아이콘 — 시안의 순환 배정을 key 로 고정). 표에 없는 key = 색 없음(글자색 상속).
const ICON_TONES = {
  home: 'blue', docauto: 'blue', collabtools: 'blue', board: 'blue', capa: 'blue', 'work-customers': 'blue',
  help: 'purple', messenger: 'purple', dxboard: 'purple', tickets: 'purple',
  'center-perf': 'purple', 'center-all': 'purple', 'center-daily': 'purple', 'center-shippers': 'purple',
  my: 'orange', drive: 'orange', execmeeting: 'orange', taskpm: 'orange', admin: 'orange', 'inbound-capa': 'orange',
  chat: 'green', office: 'green', apps: 'green', supportboard: 'green', 'abc-analysis': 'green',
  generated: 'red', calendar: 'red', directory: 'red', contracts: 'red', 'claim-register': 'red', 'claim-standards': 'red', 'claim-reasons': 'red',
  meetings: 'pink', mail2: 'pink', inquiries: 'pink', pdas: 'pink',
  documents: 'yellow', talk: 'yellow', devreq: 'yellow', 'dispatch-calc': 'yellow',
};
// 아이콘 색은 라이트 hex 그대로 — tabler 의 color prop 은 SVG stroke «속성»으로 들어가 var() 를 믿을 수 없다(WebKit 미측정).
//   원 바탕(soft)은 CSS 라 var() + 폴백 hex.
const TONE_HEX = { blue: '#2383e2', purple: '#a080c2', orange: '#d28b50', green: '#539b77', red: '#d66b60', pink: '#bb799e', yellow: '#c99630' };
const TONE_SOFT = { blue: '#eaf3fc', purple: '#eee5f5', orange: '#f9eadc', green: '#dcebe2', red: '#f8e4e0', pink: '#f3e1eb', yellow: '#f8e8b3' };
// AI 그룹 메뉴는 아이콘을 원 안에 둔다(시안 .round · 메뉴 줄만 — 하위 메뉴·상단 바 현재 위치 아이콘은 원 없이).
const ROUND_ICON_KEYS = new Set(['chat', 'generated', 'meetings', 'documents', 'docauto']);
// 🔴 아이콘 색 고르기도 이 함수 한 곳으로만 한다(iconFor 와 같은 center: 접두어 폴백). null = 색 없음.
const toneFor = (key) => {
  const name = ICON_TONES[key] || (String(key).startsWith('center:') ? 'purple' : null);
  if (!name) return null;
  return { color: TONE_HEX[name], soft: `var(--gw-${name}-soft, ${TONE_SOFT[name]})`, round: ROUND_ICON_KEYS.has(key) };
};
// 메뉴 아이콘 그리기(메뉴 줄·하위 메뉴 공용) — 색은 toneFor 가 준 tone 만 쓴다(권한 요청 줄은 tone 없음 = 회색).
//   시안 F `.menu-icon` = 19×19 상자 안에 16px 아이콘(g1-008). round=true 인 자리(데스크톱·드로어 메뉴 줄)는 언제나 19px 상자에 담고,
//   tone.round(AI 그룹)일 때만 상자에 반지름 50% + 옅은 바탕(.round)을 더한다. round=false(하위 메뉴 등)는 상자 없이 size 그대로.
const renderMenuIcon = (Icon, tone, size, round = false) => {
  if (!Icon) return null;
  const icon = <Icon size={round ? 16 : size} stroke={1.65} color={tone ? tone.color : undefined} style={{ flexShrink: 0 }} />;
  if (!round) return icon;
  const inCircle = !!tone && tone.round;
  return (
    <span aria-hidden="true" style={{ width: '19px', height: '19px', ...(inCircle ? { borderRadius: '50%', background: tone.soft } : {}), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </span>
  );
};
// 'mail'(옛 NC Mail)은 DEFAULT_NAV 에서 빠져 이 Set 으로 오지도 않는다 — 함께 지운다(잔재가 다음 사람을 헷갈리게 한다).
const ACCESS_REQUEST_KEYS = new Set(['messenger', 'drive', 'office', 'calendar', 'mail2', 'talk', 'collabtools', 'tickets']);
const SIDEBAR_SCROLL_KEY = 'gwSidebarScrollTop';
const SIDEBAR_PREF_EVENT = 'gw-sidebar-pref';
// 접어 둔 메뉴 그룹 이름들(JSON 배열) — 이 브라우저에만 기억한다(«내 즐겨찾기» 접기와 같다). 키가 없으면 전부 펼침.
//   그룹은 이름으로 기억한다 — 관리자 설정도 같은 이름 그룹을 버려(sidebarMenu mergeMissingDefaults) 사이드바 안에서 이름이 유일하다.
const CLOSED_GROUPS_KEY = 'sidebarClosedGroups';
// 저장값 → 비지 않은 문자열만 · 중복 없이. 배열이 아니거나 깨진 값이면 [](전부 펼침) — 저장값 때문에 사이드바가 죽지 않게.
const parseClosedGroups = (raw) => {
  try {
    const v = JSON.parse(raw);
    if (!Array.isArray(v)) return [];
    return v.filter((x, i) => typeof x === 'string' && x !== '' && v.indexOf(x) === i);
  } catch (e) {
    return [];
  }
};
const NAV_SCROLL_RESTORE_MAX_MS = 5000;
const NAV_SCROLL_RESTORE_EARLY_FRAMES = 30;
// 데스크톱 메뉴 글자(px) — 메뉴 줄(navBtn)·하위 메뉴 / 그룹 머리글·«내 즐겨찾기» 머리글. 모바일 드로어(15·11)는 따로 둔다.
const NAV_FONT_PX = 14;
const NAV_SECTION_FONT_PX = 12;
// 접기 머리글(«내 즐겨찾기»·메뉴 그룹 공용) 모양 — 글자 왼쪽 · 화살표 오른쪽 끝. 🔴 border 는 'none'(jsdom 이 버려 borderTop '' — 그룹 머리글 시험이 본다).
const foldHeaderStyle = {
  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px',
  fontSize: `${NAV_SECTION_FONT_PX}px`, fontWeight: 500, color: 'var(--gw-muted, #92918d)', letterSpacing: '.2px', padding: '14px 10px 5px',
  border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
  boxSizing: 'border-box', WebkitTapHighlightColor: 'transparent',
};
// 안읽음 저장소(collabUnreadStore)의 값이 누구 것인지 — 사이드바 벌 사이에 공유(모듈 한 벌)한다.
let unreadOwnerId = null;

const readSavedNavScrollTop = () => {
  try {
    const saved = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    if (saved === null) return null;
    const scrollTop = Number(saved);
    return Number.isFinite(scrollTop) ? Math.max(0, scrollTop) : null;
  } catch (e) {
    return null;
  }
};

const isSameScrollTop = (a, b) => Math.abs(Number(a || 0) - Number(b || 0)) < 1;

const requestNavScrollFrame = (callback) => {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    return { type: 'raf', id: window.requestAnimationFrame(callback) };
  }
  return { type: 'timeout', id: window.setTimeout(callback, 16) };
};

const cancelNavScrollFrame = (frame) => {
  if (!frame || typeof window === 'undefined') return;
  if (frame.type === 'raf' && typeof window.cancelAnimationFrame === 'function') {
    window.cancelAnimationFrame(frame.id);
  } else {
    window.clearTimeout(frame.id);
  }
};

/**
 * 공유 사이드바(헤더화) — AI 채팅·AI 노트 동일 레이아웃.
 *  - 세로 nav: 섹션 그룹(AI/협업/소통·업무/관리). 현재 앱은 아코디언, 나머지는 페이지 이동.
 *  - 현재 앱의 콘텐츠(children)는 "그 앱 nav 바로 아래"에 펼쳐지고, 접으면 함께 사라진다.
 *  - nav+콘텐츠는 한 스크롤(flex:1), 머리(워드마크·홈·검색·알림)와 하단(«OURi와 새 대화»)은 고정.
 *    사용량·계정·로그아웃은 상단 바 «내 계정» 창(f8a2 · 모바일도 같다 f8b2) · 화면 전환·글자 크기는 하단 알약 아래 보기 줄(PC·모바일 공통 · 계정 창엔 없다).
 * 서랍 모드(≤900 · 모바일 포함): 같은 aside 가 화면 밖 서랍(MobileDrawer)이 되고 상단 바 «메뉴 열기»(·모바일은 하단 탭바 «메뉴»)로 연다 — 모바일 전용으로 따로 그린 메뉴 목록·바닥 단추는 없다(f8b2).
 * 메뉴 추가/수정은 utils/sidebarMenu.js DEFAULT_NAV 한 곳만 고치면 반영된다.
 *  - topbarEl = AppShell 상단 바 DOM — 있으면 현재 위치·화면 이름 안내를 그 안에 포털로 그린다.
 *  - active = 지금 보이는 사이드바인가(기본 true). false = 숨겨 둔 keep-alive 사본(임베드 숨은 슬롯·보조 슬롯) —
 *    스크롤 위치를 저장하지 않고, 다시 true 가 되는 순간 저장값으로 스크롤을 되돌린다.
 */
export default function AppSidebar({ app, children, topbarEl, active = true }) {
  const navigate = useNavigate();
  // 센터별 메뉴(center:<token>)의 **이름 목록** — 서버가 권한 있는 센터만 준다(can_view_all 은 안 쓴다).
  // 보이는지는 다른 메뉴처럼 canSeeMenu(visible_menus) 하나로 정한다(2026-10-23 마스터 «다른 메뉴처럼»).
  const [centerNav, setCenterNav] = useState({ centers: [] });
  useEffect(() => {
    if (typeof centerCostAPI?.myCenters !== 'function') return undefined;
    let alive = true;
    centerCostAPI.myCenters()
      .then((r) => { if (alive) setCenterNav(r.data || { centers: [] }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  // 관리자 메뉴 설정(순서·그룹·이름·숨김) — 캐시로 먼저 그리고, 서버 값으로 갱신한다.
  // 실패하면 조용히 기본/캐시 메뉴 그대로(로그인 화면·오프라인에서 사이드바가 깨지지 않게).
  const [menuConfig, setMenuConfig] = useState(() => readCachedConfig());
  useEffect(() => {
    // 🔴 가드 필수 — AppSidebar 를 그리는 기존 시험들이 client 를 sidebarMenuAPI 없이 목한다.
    //    get() 이 undefined 를 돌려줘도 .then 에서 죽지 않게 Promise.resolve().then 으로 감싼다.
    if (typeof sidebarMenuAPI?.get !== 'function') return undefined;
    let alive = true;
    Promise.resolve().then(() => sidebarMenuAPI.get()).then((r) => {
      if (!alive || !r || !r.data) return;
      const cfg = r.data.config && typeof r.data.config === 'object' ? r.data.config : null;
      writeCachedConfig(cfg);   // 같은 창의 다른 사이드바도 이벤트로 따라온다
      setMenuConfig(cfg);       // 캐시 쓰기가 실패해도 이 값이 이긴다(마지막 setState)
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    const onMenuConfig = () => setMenuConfig(readCachedConfig());
    window.addEventListener(SIDEBAR_MENU_EVENT, onMenuConfig);
    return () => window.removeEventListener(SIDEBAR_MENU_EVENT, onMenuConfig);
  }, []);
  const location = useLocation();
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [usageOpen, setUsageOpen] = useState(() => localStorage.getItem('usageOpen') !== 'false');
  // 폭: 사용자가 끌어 저장한 값(200~480)이 있으면 그 값 · 없으면(null) 시안 F 기본 — 1201 이상 238 · 901~1200 218(f8b).
  const [savedWidth, setSidebarWidth] = useState(() => { const s = parseInt(localStorage.getItem('sidebarWidth')); return (s >= 200 && s <= 480) ? s : null; });
  // 기본 폭 판정 — 수동 «웹» 전환이면 useIsMobile 이 질의와 무관하게 false 라 늘 238 · 터치·수동 «모바일» 이면 true 지만 그때는 서랍(폭 DRAWER_W)이라 이 값을 안 쓴다.
  const midWidth = useIsMobile('(max-width: 1200px)');
  const sidebarWidth = savedWidth ?? (midWidth ? 218 : 238);
  const [collapsedPref, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');   // 접기(아이콘만)
  const COLLAPSED_W = 62;
  const mobile = useIsMobile();                         // 모바일(≤768px · 터치 · 수동 «모바일»): 서랍 + 하단 탭바
  // 좁은 폭(≤900 · f8b · 시안 F ≤900): 사이드바가 화면 밖 서랍(238)이 되고 상단 바 메뉴 단추로 연다. 모바일(f8b2)도 같은 서랍이다 — 하단 탭바만 모바일에 더해진다.
  //   narrow ⊇ mobile(같은 터치·수동 조건에 질의만 넓다)이지만 모의 환경에서 둘이 어긋나도 모바일이 서랍이 되도록 `mobile ||` 를 둔다.
  const narrow = useIsMobile('(max-width: 900px)');
  const drawerMode = mobile || narrow;
  const collapsed = collapsedPref && !drawerMode;       // 서랍은 늘 펼친 모양 — 접기 설정은 지우지 않고 넓어지면 다시 따른다
  const [drawerOpen, setDrawerOpen] = useState(false);  // 서랍 열림(모바일·좁은 폭 공통)
  // 모드가 바뀌면(모바일↔웹·폭 768·900 경계) 서랍을 닫는다 — 열린 채 다른 모드로 넘어가지 않게.
  useEffect(() => { setDrawerOpen(false); }, [mobile, drawerMode]);
  // 경로·쿼리가 바뀌면(같은 라우트 안 이동 포함) 서랍·드로어를 닫는다 — 메뉴 단추 말고 «OURi와 새 대화»·알림 이동 등 어느 길로든(f8b).
  useEffect(() => { setDrawerOpen(false); }, [location.key]);
  // 포커스(서랍 모드 — 모바일 포함): 열리면 서랍 안 «메뉴 닫기» 로 · 열려 있다가 닫히면 상단 바 «메뉴 열기» 로 되돌린다. 데스크톱(서랍 아님)은 건드리지 않는다.
  const menuOpenBtnRef = useRef(null);
  const tabAnchorRef = useRef(null);   // 이 사이드바가 그린 하단 탭바 바로 앞의 숨은 표지 — 서랍을 닫을 때 초점 폴백이 문서 전체가 아니라 이 표지 뒤의 탭바에서만 찾는다
  const menuCloseBtnRef = useRef(null);
  const drawerWasOpenRef = useRef(false);
  // 직전 렌더의 모드 — 모드가 바뀐 렌더에는 이전 모드의 drawerOpen 이 남아 있어(닫는 effect 가 아직 안 돌았다) 포커스를 옮기면 안 된다.
  const prevFocusModeRef = useRef({ mobile, drawerMode });
  useEffect(() => {
    const prev = prevFocusModeRef.current;
    const sameMode = prev.mobile === mobile && prev.drawerMode === drawerMode;
    prevFocusModeRef.current = { mobile, drawerMode };
    if (!drawerMode || !sameMode) {
      drawerWasOpenRef.current = false;
      // 모드가 바뀌어 서랍이 닫히는 길(모바일→769~900 등)에 초점이 서랍 안에 있었으면 풀어 준다 — 닫힌(visibility:hidden) 서랍 안에 초점이 남지 않게.
      //   «메뉴 열기» 로 옮기지는 않는다(사용자가 연 것이 아니라 모드가 바뀌어 닫힌 것).
      if (!sameMode) {
        const ae = document.activeElement;
        if (ae && ae !== document.body && ae.closest && ae.closest('[role="dialog"][aria-label="전체 메뉴"]') && typeof ae.blur === 'function') ae.blur();
      }
      return undefined;
    }
    if (drawerOpen) {
      drawerWasOpenRef.current = true;
      // 서랍 패널은 visibility 가 .25s 전환이라 열린 직후엔 아직 hidden 이라 focus() 가 무시될 수 있다 →
      //   시도 후 «메뉴 닫기» 가 활성 요소가 아니면 50ms 간격으로 다시(최대 8회 · 400ms).
      let tries = 0;
      let timer = null;
      const attempt = () => {
        timer = null;
        const btn = menuCloseBtnRef.current;
        if (!btn) return;
        btn.focus();
        if (document.activeElement === btn || tries >= 8) return;
        tries += 1;
        timer = setTimeout(attempt, 50);
      };
      timer = setTimeout(attempt, 0);
      return () => { if (timer != null) clearTimeout(timer); timer = null; };
    }
    if (drawerWasOpenRef.current) {
      drawerWasOpenRef.current = false;
      // 상단 바 «메뉴 열기» 가 없으면(모바일 임베드 — 상단 바 없음) 이 사이드바가 그린 하단 탭바 «전체 메뉴 열기» 로 되돌린다. 그것도 없으면 옮기지 않는다.
      //   문서 전체에서 찾지 않는다 — EmbedHost 가 방문한 임베드 사본을 display:none 슬롯에 남겨 두므로 숨은 사본의 탭바 단추를 잡는다(적대 #2 M1).
      //   탭바 바로 앞 표지(tabAnchorRef)의 뒤 형제 중 첫 «모바일 주요 메뉴» 만 본다 = 이 사이드바가 그린 탭바.
      let tabMenuBtn = null;
      for (let el = tabAnchorRef.current && tabAnchorRef.current.nextElementSibling; el && !tabMenuBtn; el = el.nextElementSibling) {
        if (el.matches('nav[aria-label="모바일 주요 메뉴"]')) { tabMenuBtn = el.querySelector('button[aria-label="전체 메뉴 열기"]'); break; }
      }
      const back = menuOpenBtnRef.current || tabMenuBtn;
      if (back) back.focus();
    }
    return undefined;
  }, [mobile, drawerMode, drawerOpen]);
  const split = useSplitView();
  const publishSidebarPref = (key, value) => {
    try { localStorage.setItem(key, String(value)); } catch (e) {}
    window.dispatchEvent(new CustomEvent(SIDEBAR_PREF_EVENT, { detail: { key, value } }));
  };
  const toggleCollapse = () => setCollapsed((c) => { const n = !c; publishSidebarPref('sidebarCollapsed', n); return n; });
  const [myUsage, setMyUsage] = useState({});
  const [unread, setUnread] = useState(() => getUnread());   // 사이드바 안읽음 숫자·점(collabUnreadStore 구독)
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationUnread, setNotificationUnread] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  // 좁은 폭 서랍에서 머리 알림 창은 body 포털로 그린다(서랍 패널의 overflow·transform 에 잘리지 않게) — 단추 아래 위치는 열 때 재서 둔다.
  const notifBtnRef = useRef(null);
  const [notifTop, setNotifTop] = useState(0);
  // 서랍이 닫히면(서랍 모드) 알림 창도 닫는다 — 포털이라 패널의 visibility:hidden 이 가려 주지 않는다.
  useEffect(() => { if (drawerMode && !drawerOpen) setNotificationOpen(false); }, [drawerMode, drawerOpen]);
  // 모드가 바뀌면(모바일↔웹·폭 768·900 경계) 알림 창도 닫는다 — 서랍에서 연 채 넓어지면 데스크톱 머리에 다시 뜨고, 포털이 top 0 에 한 프레임 걸리는 것도 막는다.
  useEffect(() => { setNotificationOpen(false); }, [mobile, drawerMode]);
  const navScrollRef = useRef(null);
  const pendingNavScrollRestoreRef = useRef(null);
  const navScrollRestoreFrameRef = useRef(null);
  const navScrollRestoreTimeoutRef = useRef(null);
  const navScrollRestoreResizeObserverRef = useRef(null);
  const navScrollRestoreMutationObserverRef = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  const cancelNavScrollRestoreWatchers = useCallback(() => {
    if (navScrollRestoreFrameRef.current) {
      cancelNavScrollFrame(navScrollRestoreFrameRef.current);
      navScrollRestoreFrameRef.current = null;
    }
    if (navScrollRestoreTimeoutRef.current !== null) {
      window.clearTimeout(navScrollRestoreTimeoutRef.current);
      navScrollRestoreTimeoutRef.current = null;
    }
    if (navScrollRestoreResizeObserverRef.current) {
      navScrollRestoreResizeObserverRef.current.disconnect();
      navScrollRestoreResizeObserverRef.current = null;
    }
    if (navScrollRestoreMutationObserverRef.current) {
      navScrollRestoreMutationObserverRef.current.disconnect();
      navScrollRestoreMutationObserverRef.current = null;
    }
  }, []);

  const clearPendingNavScrollRestore = useCallback(() => {
    cancelNavScrollRestoreWatchers();
    pendingNavScrollRestoreRef.current = null;
  }, [cancelNavScrollRestoreWatchers]);

  const finishPendingNavScrollRestore = useCallback(() => {
    const state = pendingNavScrollRestoreRef.current;
    cancelNavScrollRestoreWatchers();
    if (!state) return;
    state.completed = true;
    navScrollRestoreFrameRef.current = requestNavScrollFrame(() => {
      navScrollRestoreFrameRef.current = null;
      if (pendingNavScrollRestoreRef.current === state) pendingNavScrollRestoreRef.current = null;
    });
  }, [cancelNavScrollRestoreWatchers]);

  const applyPendingNavScrollRestore = useCallback(() => {
    const state = pendingNavScrollRestoreRef.current;
    if (!state || navScrollRef.current !== state.node) return true;
    state.node.scrollTop = state.targetY;
    state.lastAppliedTop = state.node.scrollTop;
    return isSameScrollTop(state.lastAppliedTop, state.targetY);
  }, []);

  const schedulePendingNavScrollRestoreRetry = useCallback(() => {
    if (navScrollRestoreFrameRef.current) return;
    navScrollRestoreFrameRef.current = requestNavScrollFrame(() => {
      navScrollRestoreFrameRef.current = null;
      const state = pendingNavScrollRestoreRef.current;
      if (!state || state.completed || navScrollRef.current !== state.node) return;
      const restored = applyPendingNavScrollRestore();
      if (restored) {
        finishPendingNavScrollRestore();
        return;
      }
      state.frameAttempts += 1;
      if (state.frameAttempts < NAV_SCROLL_RESTORE_EARLY_FRAMES) {
        schedulePendingNavScrollRestoreRetry();
      }
    });
  }, [applyPendingNavScrollRestore, finishPendingNavScrollRestore]);

  const startPendingNavScrollRestoreWatchers = useCallback(() => {
    const state = pendingNavScrollRestoreRef.current;
    if (!state || state.watchersStarted) return;
    state.watchersStarted = true;
    const { node } = state;

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => schedulePendingNavScrollRestoreRetry());
      const observeResize = (element) => {
        if (element && element.nodeType === 1) {
          try { resizeObserver.observe(element); } catch (e) {}
        }
      };
      observeResize(node);
      Array.from(node.children).forEach(observeResize);
      navScrollRestoreResizeObserverRef.current = resizeObserver;
    }

    if (typeof MutationObserver !== 'undefined') {
      const mutationObserver = new MutationObserver((mutations) => {
        if (navScrollRestoreResizeObserverRef.current) {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((addedNode) => {
              if (addedNode.nodeType === 1) {
                try { navScrollRestoreResizeObserverRef.current.observe(addedNode); } catch (e) {}
              }
            });
          });
        }
        schedulePendingNavScrollRestoreRetry();
      });
      mutationObserver.observe(node, { childList: true, subtree: true });
      navScrollRestoreMutationObserverRef.current = mutationObserver;
    }

    navScrollRestoreTimeoutRef.current = window.setTimeout(() => {
      if (pendingNavScrollRestoreRef.current === state) {
        pendingNavScrollRestoreRef.current = null;
      }
      cancelNavScrollRestoreWatchers();
    }, NAV_SCROLL_RESTORE_MAX_MS);
    schedulePendingNavScrollRestoreRetry();
  }, [cancelNavScrollRestoreWatchers, schedulePendingNavScrollRestoreRetry]);

  const restoreNavScrollFromStorage = useCallback((node) => {
    const targetY = readSavedNavScrollTop();
    clearPendingNavScrollRestore();
    if (targetY === null) return;

    pendingNavScrollRestoreRef.current = {
      node,
      targetY,
      lastAppliedTop: null,
      frameAttempts: 0,
      watchersStarted: false,
      completed: false,
    };
    const restored = applyPendingNavScrollRestore();
    if (restored) {
      finishPendingNavScrollRestore();
    } else {
      startPendingNavScrollRestoreWatchers();
    }
  }, [applyPendingNavScrollRestore, clearPendingNavScrollRestore, finishPendingNavScrollRestore, startPendingNavScrollRestoreWatchers]);

  const setNavScrollRef = useCallback((node) => {
    if (navScrollRef.current && navScrollRef.current !== node) clearPendingNavScrollRestore();
    navScrollRef.current = node;
    if (!node) return;
    restoreNavScrollFromStorage(node);
  }, [clearPendingNavScrollRestore, restoreNavScrollFromStorage]);
  const saveNavScrollTop = useCallback((e) => {
    // 숨은 사본(active=false)은 저장값을 덮어쓰지 않는다 — 지금 보이는 사이드바의 위치가 정본이다.
    if (!activeRef.current) return;
    const state = pendingNavScrollRestoreRef.current;
    const currentTop = e.currentTarget.scrollTop;
    if (state && state.node === e.currentTarget) {
      if (isSameScrollTop(currentTop, state.lastAppliedTop) || isSameScrollTop(currentTop, state.targetY)) return;
      clearPendingNavScrollRestore();
    }
    try { sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(currentTop)); } catch (err) {}
  }, [clearPendingNavScrollRestore]);
  useLayoutEffect(() => {
    if (navScrollRef.current) restoreNavScrollFromStorage(navScrollRef.current);
  }, [restoreNavScrollFromStorage]);
  useEffect(() => {
    return () => clearPendingNavScrollRestore();
  }, [clearPendingNavScrollRestore]);
  // 숨겨 두었다 다시 보이는 사이드바(임베드 keep-alive 슬롯 · 보조→주 전환)는 다시 마운트되지 않아 위 복원이 안 돈다 —
  //   보이게 되는 순간 저장값(직전 화면 사이드바가 마지막으로 쓴 위치)으로 되돌린다. 숨으면 대기 중인 복원을 거둔다.
  //   🔴 훅이라 아래 `split.renderSlot === 'side'` 의 return null 앞에 둔다.
  const wasActiveRef = useRef(active);
  useLayoutEffect(() => {
    const was = wasActiveRef.current;
    wasActiveRef.current = active;
    if (!active) { clearPendingNavScrollRestore(); return; }
    if (!was && navScrollRef.current) restoreNavScrollFromStorage(navScrollRef.current);
  }, [active, clearPendingNavScrollRestore, restoreNavScrollFromStorage]);
  useEffect(() => {
    const onSidebarPref = (e) => {
      const { key, value } = e.detail || {};
      if (key === 'usageOpen') setUsageOpen(value);
      if (key === 'sidebarCollapsed') setCollapsed(value);
      if (key === 'sidebarFavoritesOpen') setFavOpen(value);
      if (key === CLOSED_GROUPS_KEY) setClosedGroups(parseClosedGroups(value));
    };
    const onStorage = (e) => {
      if (e.key === 'usageOpen') setUsageOpen(e.newValue !== 'false');
      if (e.key === 'sidebarCollapsed') setCollapsed(e.newValue === 'true');
      if (e.key === 'sidebarFavoritesOpen') setFavOpen(e.newValue !== 'false');
      if (e.key === CLOSED_GROUPS_KEY) setClosedGroups(parseClosedGroups(e.newValue));
    };
    window.addEventListener(SIDEBAR_PREF_EVENT, onSidebarPref);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SIDEBAR_PREF_EVENT, onSidebarPref);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  useEffect(() => { chatAPI.getMyUsage().then((r) => setMyUsage(r.data || {})).catch(() => {}); }, []);
  useEffect(() => {
    if (typeof chatAPI.getUnreadNotificationCount !== 'function') return undefined;
    const poll = () => {
      if (document.hidden) return;
      chatAPI.getUnreadNotificationCount()
        .then((r) => setNotificationUnread(Number(r.data?.count || 0)))
        .catch(() => {});
    };
    poll();
    const iv = setInterval(poll, 60000);
    const onFocus = () => poll();
    const onVis = () => { if (!document.hidden) poll(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVis); };
  }, []);
  // 안읽음(메신저 멘션+DM / 메일 INBOX) — 값과 폴러는 collabUnreadStore 한 벌이다(숨은 임베드 사이드바까지
  // 여러 벌이어도 폴러 1벌 · 중복 요청은 저장소가 합친다). 숨은 탭 건너뛰기(부하감사 F3)도 저장소가 한다.
  // 값은 권한과 무관하게 구독한다 — 숨은 사이드바가 옛 user 를 들고 있어도 벌마다 표시가 어긋나지 않게.
  useEffect(() => {
    setUnread(getUnread());
    return subscribeUnread(setUnread);
  }, []);
  // 메일 숫자 = 메일 메뉴(mail2) 권한 — 드라이브 권한과 무관(마스터 지시 2026-08-24 · 서버 /unread 와 같은 판정).
  const unreadGate = user.role === 'admin' || !!user.messenger_access || !!user.drive_access || canSeeMenu(user, 'mail2');
  // 사용자가 바뀌면(로그아웃 → 다른 계정 로그인은 SPA 이동이라 저장소 값이 남는다) 앞 사람의 점을 비운다.
  // 🔴 폴러 참조보다 먼저 둔다 — 같은 커밋에서 새 사용자 요청이 출발한 뒤 비우면 그 요청까지 버린다.
  useEffect(() => {
    if (user.id === undefined || user.id === null) return;
    const id = String(user.id);
    if (unreadOwnerId !== null && unreadOwnerId !== id) {
      resetUnread();
      if (unreadGate) refreshUnread();   // 새 사용자 값을 바로 받는다(마운트 때는 아래 폴러 시작과 합쳐진다)
    }
    unreadOwnerId = id;
  }, [user.id]);
  // 권한 있을 때만 폴러 참조를 건다(첫 참조 = 즉시 1회 + 45초 + 창 포커스·탭 보임).
  useEffect(() => {
    if (!unreadGate) return undefined;
    return startUnreadPolling();
  }, [unreadGate]);
  // 화면을 옮기면 1회 — 다른 기기에서 읽고 이 화면으로 왔을 때의 옛 점 제거. 권한 있을 때만(없으면 요청 0).
  useEffect(() => {
    if (unreadGate) refreshUnread();
  }, [location.pathname, unreadGate]);

  // 다른 탭에서 로그아웃(localStorage.clear/토큰 제거)되면 이 탭도 로그인 화면으로(세션 일관, 부하감사 F5).
  useEffect(() => {
    const onStorage = (e) => {
      if ((e.key === 'token' && !e.newValue) || (e.key === null && !localStorage.getItem('token'))) {
        navigate('/login');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [navigate]);
  // 서버에서 최신 권한(메신저·드라이브·DB) 갱신 → localStorage 반영(재로그인 없이 메뉴 게이트 최신화)
  useEffect(() => {
    authAPI.getMe().then((r) => {
      const d = r.data || {};
      setUser((prev) => {
        const merged = { ...prev, id: d.id, name: d.name, email: d.email, role: d.role,
          db_access: d.db_access, messenger_access: d.messenger_access, drive_access: d.drive_access,
          menu_access: d.menu_access || {}, ui_font: d.ui_font };
        if (Array.isArray(d.visible_menus)) merged.visible_menus = d.visible_menus;
        else delete merged.visible_menus;
        try { localStorage.setItem('user', JSON.stringify(merged)); } catch (e) {}
        // 🔴 merged 가 아니라 **서버 응답 d** 를 넘긴다. merged 는 prev(localStorage)를 밑에 깔기 때문에,
        //    여기에 ui_font 를 안 실으면 stale 값이 그대로 적용되고 캐시에도 다시 써져
        //    "다른 기기에서 바꾼 폰트가 따라온다"가 영원히 깨진다(2026-08-20 e2e 로 발견).
        syncFontFromUser(d);
        return merged;
      });
    }).catch(() => {});
  }, []);

  // ── 메뉴 즐겨찾기(사용자별 · 서버 user_menu_favorites) — 캐시(같은 uid 일 때만)로 먼저 그리고 서버 값으로 갱신 ──
  // 🔴 이 블록의 훅은 전부 아래 `split.renderSlot === 'side'` 의 return null 앞에 둔다(훅 순서).
  const [favKeys, setFavKeys] = useState(() => readCachedFavorites(user.id) ?? []);
  const favKeysRef = useRef(favKeys);      // 갱신 함수 안에서 값을 꺼내지 않는다 — 최신 배열은 ref 로 읽는다
  const uidRef = useRef(user.id);
  uidRef.current = user.id;
  const [favEdit, setFavEdit] = useState(false);      // «메뉴 즐겨찾기 편집» 상태 — ☆/★ 는 이때만 DOM 에 있다
  const [favFrozen, setFavFrozen] = useState(null);   // 편집 중 묶음 고정(누르는 사이 메뉴가 밀리지 않게)
  // «내 즐겨찾기» 묶음 접기 — 이 브라우저에 기억(기본 펼침) · 다른 사이드바와 publishSidebarPref 로 맞춘다
  const [favOpen, setFavOpen] = useState(() => { try { return localStorage.getItem('sidebarFavoritesOpen') !== 'false'; } catch (e) { return true; } });
  const toggleFavOpen = () => { const n = !favOpen; setFavOpen(n); publishSidebarPref('sidebarFavoritesOpen', n); };
  // 메뉴 그룹 접기(관리자 설정 그룹 포함) — 접은 그룹 이름을 이 브라우저에 기억(기본 전부 펼침) · 다른 사이드바와 publishSidebarPref 로 맞춘다
  const [closedGroups, setClosedGroups] = useState(() => { try { return parseClosedGroups(localStorage.getItem(CLOSED_GROUPS_KEY)); } catch (e) { return []; } });
  const toggleGroupOpen = (name) => {
    const n = closedGroups.includes(name) ? closedGroups.filter((g) => g !== name) : [...closedGroups, name];
    setClosedGroups(n);
    publishSidebarPref(CLOSED_GROUPS_KEY, JSON.stringify(n));
  };
  // 화면 값 바꾸기는 이 함수로만 — 캐시 쓰기 먼저, setState 마지막(캐시 쓰기가 실패해도 이 값이 이긴다).
  const applyFavKeys = (keys) => {
    favKeysRef.current = keys;
    writeCachedFavorites(uidRef.current, keys);
    setFavKeys(keys);
  };
  // 마지막 토글이 끝난 뒤 서버 값을 새로 읽는다. 그 사이 다른 토글·동기화가 있었으면 버리고,
  // 나가 있는 토글이 없으면 한 번 더 읽는다(left = 남은 횟수). 🔴 alive 가드 없음 — 언마운트돼도 캐시로 전달된다.
  const reloadFavorites = (left) => {
    const seq = favoriteSeq();
    Promise.resolve().then(() => sidebarMenuAPI.getFavorites?.()).then((r) => {
      if (!Array.isArray(r?.data?.keys)) return;
      if (!favoritesBusy() && favoriteSeq() === seq) { applyFavKeys(r.data.keys); return; }
      if (!favoritesBusy() && left > 1) reloadFavorites(left - 1);
    }).catch(() => {});
  };
  const onToggleFavorite = (key) => {
    const cur = favKeysRef.current;
    const on = !cur.includes(key);
    if (on && cur.length >= MAX_FAVORITES) return;
    const next = toggleFavorite(cur, key);
    if (typeof sidebarMenuAPI?.toggleFavorite !== 'function') { applyFavKeys(next); return; }
    beginFavoriteSave();
    applyFavKeys(next);
    // 🔴 POST 응답의 keys 는 화면에 쓰지 않는다 — 응답 도착 순서가 서버 커밋 순서와 같다는 보장이 없다.
    //    성공·실패 무관하게 나가 있는 토글이 0 이 되면 서버 값을 다시 읽는다. 두 인자 꼴(감소가 두 번 일어나지 않게).
    const onDone = () => {
      if (!endFavoriteSave()) return;
      reloadFavorites(2);
    };
    Promise.resolve().then(() => sidebarMenuAPI.toggleFavorite(key, on)).then(onDone, onDone);
  };
  const endFavEdit = () => { setFavEdit(false); setFavFrozen(null); };
  const startFavEdit = () => { setFavFrozen(favKeysRef.current); setFavEdit(true); };
  useEffect(() => {
    // 🔴 가드 필수 — AppSidebar 를 그리는 기존 시험들이 client 를 getFavorites 없이 목한다.
    if (typeof sidebarMenuAPI?.getFavorites !== 'function') return undefined;
    let alive = true;
    const startSeq = favoriteSeq();
    Promise.resolve().then(() => sidebarMenuAPI.getFavorites()).then((r) => {
      // 요청 뒤 어느 사이드바에서도 토글·동기화가 없었을 때만 쓴다(늦게 온 옛 값이 토글 결과를 덮지 않게).
      if (!alive || favoritesBusy() || favoriteSeq() !== startSeq) return;
      if (!Array.isArray(r?.data?.keys)) return;
      applyFavKeys(r.data.keys);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    // 같은 창의 다른 사이드바(이벤트)·다른 탭(storage) 이 캐시를 바꾸면 따라온다. 캐시는 다시 쓰지 않는다(되먹임 방지).
    const pull = () => {
      const k = readCachedFavorites(uidRef.current);
      if (k === null) return;   // 남의 uid·uid 없음
      if (JSON.stringify(k) === JSON.stringify(favKeysRef.current)) return;
      noteFavoriteChange();
      favKeysRef.current = k;
      setFavKeys(k);
    };
    const onStorage = (e) => { if (e.key === FAVORITES_CACHE_KEY) pull(); };
    window.addEventListener(FAVORITES_EVENT, pull);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(FAVORITES_EVENT, pull);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  // 편집 상태는 드로어를 닫거나·사이드바를 접거나·어느 길로든 화면을 옮기면 끝난다(숨은 임베드 사이드바가 편집 상태로 남지 않게).
  useEffect(() => { if (!drawerOpen) endFavEdit(); }, [drawerOpen]);
  useEffect(() => { if (collapsed) endFavEdit(); }, [collapsed]);
  useEffect(() => { endFavEdit(); }, [location.pathname]);

  // 상단 바 «내 계정» 창(f8a2) — 아바타 단추로 열고, 바깥 mousedown·Escape·window blur·화면 이동으로 닫는다.
  //   window blur: iframe(임베드 화면)에 초점이 가면 부모 document 는 mousedown 을 못 받는다 → 부모 window 가 blur 된다.
  //   🔴 훅이라 아래 `split.renderSlot === 'side'` 의 return null 앞에 둔다. acctRef = 아바타 단추 + 창을 함께 감싼 요소.
  //   서랍(MobileDrawer · body 포털 · z 1100)과 이 창(셸 안 · z 1000)은 동시에 열지 않는다 — 한쪽을 열면 다른 쪽을 닫는다(openDrawer · toggleAcct).
  //   (창은 서랍 덮개 밑에 깔리고, 창의 Escape 는 document 리스너라 서랍의 Escape 와 한 번에 같이 닫혔다 → 층을 쌓지 않고 상호 배타로.)
  const [acctOpen, setAcctOpen] = useState(false);
  const acctRef = useRef(null);
  const avatarBtnRef = useRef(null);
  useEffect(() => {
    if (!acctOpen) return undefined;
    const onDown = (e) => { if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false); };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setAcctOpen(false);
      if (avatarBtnRef.current) avatarBtnRef.current.focus();
    };
    const onBlur = () => setAcctOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('blur', onBlur);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); window.removeEventListener('blur', onBlur); };
  }, [acctOpen]);
  useEffect(() => { setAcctOpen(false); }, [location.pathname]);
  // 모바일↔웹·폭 768·900 경계를 넘으면 닫는다(f8a2 이월) — 열린 채 다른 모드로 넘어가면 웹으로 돌아왔을 때 다시 보인다.
  useEffect(() => { setAcctOpen(false); }, [mobile, drawerMode]);
  // 서랍 ↔ 내 계정 창 상호 배타 — 서랍을 여는 모든 길(상단 바 «메뉴 열기» · 탭바 «메뉴»)은 openDrawer, 창을 여는 길(아바타 단추)은 toggleAcct.
  //   창 안 «즐겨찾기 편집 시작» 은 창을 닫고 서랍을 여는 쪽이라 이미 맞다.
  const openDrawer = () => { setAcctOpen(false); setDrawerOpen(true); };
  const toggleAcct = () => { if (!acctOpen) setDrawerOpen(false); setAcctOpen(!acctOpen); };

  // base = 기본 메뉴 + 센터별 항목('센터' 머리 바로 뒤). 옛 저장 설정(센터 그룹 없음)은 withCenterGroupIfUnset 이 '센터' 그룹을,
  //   그다음 withClaimGroupIfUnset 이 '클레임' 그룹('센터' 머리 앞)을, 마지막으로 withWorkGroupIfUnset 이 '업무 통합' 그룹
  //   ('클레임' 머리 앞)을 넣는다(순서를 바꾸지 않는다)
  //   — 관리자 › 사이드바 메뉴의 미리보기와 같은 식이다(갈리면 미리보기와 실제 사이드바가 어긋난다).
  const navItems = (() => {
    const base = withCenterSites(DEFAULT_NAV, centerNav.centers);
    return resolveNav(menuConfig
      ? { ...menuConfig, entries: withWorkGroupIfUnset(withClaimGroupIfUnset(withCenterGroupIfUnset(menuConfig.entries, base), base), base) }
      : menuConfig, base);
  })();
  const canSee = (it) => canSeeMenu(user, it.key);          // 사이드바 노출 = 라우트 가드와 동일 기준(센터 메뉴 포함)
  const canRequestAccess = (it) => ACCESS_REQUEST_KEYS.has(it.key) && canSeeMenu(user, 'inquiries');
  // «내 즐겨찾기» 묶음 — 편집 중에는 편집을 시작할 때 모양 그대로(favFrozen), 완료 때 한 번에 바뀐다.
  const favItems = favoriteItems(navItems, favFrozen ?? favKeys, canSee);
  // 원래 자리 줄의 ☆/★ — 켜짐 여부는 지금 값(favKeys)을 그대로 따른다.
  const favFor = (it) => {
    const on = favKeys.includes(it.key);
    return { on, disabled: !on && favKeys.length >= MAX_FAVORITES, onClick: () => onToggleFavorite(it.key) };
  };
  // 사이드바 안읽음 숫자(시안 F .count) — 메일 메뉴 키는 'mail2'(sidebarMenu.js · 옛 'mail' 은 메뉴에 없다).
  const navCount = (it) => (it.key === 'messenger' ? (unread.messenger || 0) : it.key === 'mail2' ? (unread.mail || 0) : 0);
  const countText = (n) => (n > 99 ? '99+' : String(n));
  // 메뉴 줄 오른쪽 끝 회색 숫자(펼침·서랍 12px · 모바일 전체 메뉴 13px). 🔴 title="새 알림" 은 시험·e2e 가 이 span 을 찾는 표지라 남긴다 ·
  //   aria-label 은 달지 않는다(역할 없는 span 금지) — 화면 밖 글자 « 안읽음 N건» 으로 읽힌다.
  //   key = 메뉴 키 — 'mail2' 이고 저장소에 mail_checked_at(메인 화면 메일 위젯이 센 시각)이 있으면 안쪽 숫자 span 에만
  //   «MM-DD HH:mm 확인» title(KST)을 단다(바깥 title 은 그대로 — 안쪽이 이긴다) · 시각이 없거나 메신저면 지금과 같다.
  const renderUnreadCount = (n, px, key = null) => {
    const at = key === 'mail2' ? fmtKstShort(unread.mail_checked_at) : '';
    return (
      <span title="새 알림" style={{ marginLeft: 'auto', flexShrink: 0, fontSize: `${px}px`, fontWeight: 400, color: 'var(--gw-muted, #92918d)', fontVariantNumeric: 'tabular-nums' }}>
        <span aria-hidden="true" title={at ? `${at} 확인 · 메인 화면을 열면 다시 셉니다` : undefined}>{countText(n)}</span>
        <span style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}> 안읽음 {n}건{at ? ` · ${at} 확인` : ''}</span>
      </span>
    );
  };
  const go = (path) => { navigate(path); setDrawerOpen(false); };   // 이동 + (모바일)드로어 닫기
  const getSplitAction = (it) => {
    // 모바일 서랍(터치 태블릿·수동 «모바일» 로 폭이 ≥1100 이어도)엔 «보조 패널로 열기» 가 없다 — 기준(ac55b513)의 옛 모바일 서랍과 같다.
    if (mobile || !split.canUseSplit || !canOpenSplitApp(it.key)) return null;
    const disabled = app === it.key || split.sideApp === it.key;
    return {
      disabled,
      title: app === it.key
        ? '현재 주 패널과 같은 앱은 보조 패널로 열 수 없습니다'
        : (split.sideApp === it.key ? '이미 보조 패널에 열려 있습니다' : '보조 패널로 열기'),
      onClick: () => split.openSideApp(it.key),
    };
  };
  // 로그아웃 시 게이트웨이 토큰뿐 아니라 임베드된 메신저(MM)·드라이브(NC) 세션도 함께 종료(잔존 세션 방지).
  // 절차 본문은 lib/gatewayLogout 에 있다(임베드 실패 카드의 «다시 로그인» 도 같은 절차를 쓴다).
  const logout = async () => {
    await logoutGateway();
    navigate('/login');
  };
  const startResize = (e) => {
    e.preventDefault();
    // iframe(메신저·드라이브) 위에서 드래그하면 iframe이 mousemove를 삼켜 '늘리기'가 안 됨
    // → 드래그 동안 전체화면 투명 오버레이를 덮어 부모 문서가 마우스 이벤트를 계속 받게 한다.
    const ov = document.createElement('div');
    ov.style.cssText = 'position:fixed;inset:0;z-index:99999;cursor:col-resize';
    document.body.appendChild(ov);
    // 웹 배율(<html> zoom)이 걸리면 clientX 는 화면 px, 폭은 CSS px 다 → 손잡이의 부모(aside)로 배율을 재서 나눈다(jsdom·1.0 은 1).
    //   aside 왼쪽 끝은 화면 0 이라 clientX ÷ 배율 = CSS 폭.
    const aside = e.currentTarget.parentElement;
    const s = aside ? scaleOf(aside.getBoundingClientRect().width, aside.offsetWidth) : 1;
    const onMove = (ev) => setSidebarWidth(Math.min(480, Math.max(200, Math.round(ev.clientX / s))));
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      if (ov.parentNode) ov.parentNode.removeChild(ov);
      setSidebarWidth((w) => { if (w != null) { try { localStorage.setItem('sidebarWidth', String(w)); } catch (e2) {} } return w; });
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  if (split.renderSlot === 'side') return null;

  // ☆/★ 단추(navBtn 의 편집 상태 줄) — 접근 이름에 메뉴 이름을 넣는다(다 같은 이름이면 화면 읽기에 쓸모가 없다).
  //   🔴 이름을 그냥 «즐겨찾기» 로 하지 않는다 — e2e 가 대화 즐겨찾기를 그 이름(exact)으로 찾는다.
  const renderFavStar = (label, fav, { right, top = '50%', size, icon }) => {
    const name = `${label} 즐겨찾기 ${fav.on ? '해제' : '추가'}`;
    return (
      <button
        type="button"
        className={'gw-sidebar-fav' + (fav.on ? ' is-on' : '')}
        disabled={fav.disabled}
        aria-label={name}
        title={fav.disabled ? `즐겨찾기는 ${MAX_FAVORITES}개까지` : name}
        onClick={(e) => { e.stopPropagation(); if (!fav.disabled) fav.onClick(); }}
        style={{ position: 'absolute', right, top, transform: 'translateY(-50%)', width: `${size}px`, height: `${size}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 'none', borderRadius: '6px', background: 'var(--gw-surface, #fff)', color: fav.on ? '#fbbf24' : 'var(--gw-muted, #92918d)', opacity: fav.disabled ? 0.5 : 1, cursor: fav.disabled ? 'not-allowed' : 'pointer', boxShadow: '0 1px 4px rgba(15,23,42,.16)', lineHeight: 1, WebkitTapHighlightColor: 'transparent' }}
      >
        <IconStar size={icon} stroke={1.8} fill={fav.on ? 'currentColor' : 'none'} />
      </button>
    );
  };
  // 편집 단추(편집 «완료» 는 사이드바 바닥 알약 위 · «시작» 은 상단 바 계정 창 — 서랍 모드도 같다). 🔴 title 을 보이는 글자와 같게 준다 —
  //   e2e 가 `aside button:not([title]):has-text("즐겨찾기")` 로 대화 즐겨찾기 칩을 찾는다.
  //   afterStart = 편집을 «시작»한 뒤 부를 함수(상단 바 계정 창이 자기를 닫는다 · f8a2).
  const renderFavEditButton = (afterStart) => {
    const text = favEdit ? '즐겨찾기 편집 완료' : '☆ 메뉴 즐겨찾기 편집';
    return (
      <button
        type="button"
        onClick={favEdit ? endFavEdit : () => { startFavEdit(); if (afterStart) afterStart(); }}
        title={text}
        style={{ width: '100%', minHeight: '36px', display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 10px', border: `1px solid ${favEdit ? COLORS.primaryBorder : COLORS.border}`, borderRadius: '6px', background: favEdit ? COLORS.primarySoft : 'var(--gw-surface, #fff)', color: favEdit ? COLORS.selectedText : 'var(--gw-text, #32312e)', cursor: 'pointer', fontSize: '12px', fontWeight: 700, boxSizing: 'border-box', WebkitTapHighlightColor: 'transparent' }}
      >
        {text}
      </button>
    );
  };
  // «내 즐겨찾기» 머리글 = 묶음 접기 단추. 🔴 글자는 단추의 직속 글자 노드(span 금지 —
  //   시험 groupLabels 가 getByText 로 단추를 잡고 nextElementSibling 부터 묶음 줄을 줍는다) · 화살표는 글자 없는 svg ·
  //   title 필수(e2e 대화 즐겨찾기 칩 선택자 `:not([title])` 에서 빠지게) · gw-sidebar-btn/navrow 클래스 금지(메뉴 순서 단언).
  const renderFavHeader = () => (
    <button key="favorites-section" type="button" onClick={toggleFavOpen}
      aria-expanded={favOpen} title={favOpen ? '즐겨찾기 접기' : '즐겨찾기 펼치기'}
      style={foldHeaderStyle}>
      내 즐겨찾기
      <IconChevronDown size={14} stroke={2} aria-hidden="true"
        style={{ flexShrink: 0, transition: 'transform .15s', transform: favOpen ? 'none' : 'rotate(-90deg)' }} />
    </button>
  );
  // 메뉴 그룹 머리글 = 그 그룹 접기 단추(«내 즐겨찾기» 머리글과 같은 모양·규칙). 🔴 글자는 단추의 직속 글자 노드(span 금지 —
  //   시험이 getByText(그룹 이름) 의 style·nextElementSibling 을 본다) · title 필수 · gw-sidebar-btn/navrow 클래스 금지(메뉴 순서 단언·e2e findBtn).
  //   hiddenUnread = 접혀 안 보이는 줄의 안읽음 합계 — 접혔을 때만 머리글에 숫자로 보인다(메신저·메일 숫자가 접기로 사라지지 않게).
  const renderGroupHeader = (name, open, hiddenUnread) => (
    <button key={'sec-' + name} type="button" data-gw-nav-group={name} onClick={() => toggleGroupOpen(name)}
      aria-expanded={open} title={open ? `${name} 그룹 접기` : `${name} 그룹 펼치기`}
      style={{ ...foldHeaderStyle, position: 'relative', marginTop: 0 }}>
      {name}
      {!open && hiddenUnread > 0 && renderUnreadCount(hiddenUnread, 12)}
      <IconChevronDown size={14} stroke={2} aria-hidden="true"
        style={{ flexShrink: 0, transition: 'transform .15s', transform: open ? 'none' : 'rotate(-90deg)' }} />
    </button>
  );
  // groupAt[i] = navItems[i] 가 든 그룹 이름(첫 그룹 머리글 앞의 메인·사용 안내·내 정보는 null).
  const groupAt = [];
  navItems.forEach((x, i) => { groupAt[i] = x.section ? x.section : (i > 0 ? groupAt[i - 1] : null); });
  // 접힌 그룹인가 — 아이콘만 모드(collapsed)는 머리글이 구분선이라 접기를 무시한다(즐겨찾기 묶음과 같다 · 서랍은 collapsed=false 라 그대로 먹는다).
  const folded = (name) => !collapsed && name != null && closedGroups.includes(name);

  // fav = { on, onClick, disabled } — 원래 자리 줄에만 넘긴다(«내 즐겨찾기» 묶음 줄·권한 요청 줄은 안 넘긴다).
  // ☆/★ 는 편집 상태에서만 DOM 에 그린다 — 마우스 위치·초점으로 nav 안 단추 수가 바뀌면 안 된다.
  // splitLabel = 보조 패널 단추의 접근 이름(«내 즐겨찾기» 묶음 줄만 넘긴다 · 없으면 «<메뉴> 보조 패널로 열기»).
  // tone = 아이콘 색(toneFor(key) — 권한 요청 줄은 안 넘긴다 = 회색). 🔴 오른쪽 여백 38px·68px 은 보조 패널·☆ 단추 자리다.
  // countKey = 메뉴 키(renderUnreadCount 의 «확인 시각» 판정용 · 권한 요청 줄은 안 넘긴다).
  const navBtn = (active, label, onClick, dot, Icon, splitAction = null, fav = null, splitLabel = null, tone = null, countKey = null) => {
    const showSplit = !!splitAction && !collapsed;
    const showFav = !!fav && favEdit && !collapsed;
    const padding = collapsed ? '0' : (showFav
      ? (showSplit ? '0 68px 0 9px' : '0 38px 0 9px')
      : (showSplit ? '0 38px 0 9px' : '0 9px'));
    return (
      <div className="gw-sidebar-navrow" style={{ position: 'relative', marginBottom: '1px' }}>
        <button type="button" className="gw-sidebar-btn" onClick={onClick} title={collapsed ? label : undefined} aria-current={active ? 'page' : undefined}
          onMouseEnter={active ? undefined : (e) => { e.currentTarget.style.background = 'var(--gw-hover, #efefed)'; }}
          onMouseLeave={active ? undefined : (e) => { e.currentTarget.style.background = 'transparent'; }}
          style={{ width: '100%', minHeight: collapsed ? '34px' : '28px', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: '9px', padding, borderRadius: '5px', border: 'none', cursor: 'pointer', fontWeight: active ? 550 : 400, fontSize: `${NAV_FONT_PX}px`, background: active ? 'var(--gw-selected, #eaeae7)' : 'transparent', color: active ? 'var(--gw-text, #32312e)' : 'var(--gw-secondary, #64635f)', textAlign: 'left', position: 'relative', boxSizing: 'border-box', WebkitTapHighlightColor: 'transparent' }}>
          {renderMenuIcon(Icon, tone, 16, true)}
          {!collapsed && <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>}
          {/* dot = 안읽음 수(숫자) · 🔴 `dot > 0 &&` — `{0 && …}` 는 글자 «0» 을 그린다. 접힘(아이콘만)은 숫자 자리가 없어 빨간 점. */}
          {dot > 0 && (collapsed
            ? <span title="새 알림" style={{ position: 'absolute', top: '6px', right: '10px', width: '7px', height: '7px', borderRadius: '50%', background: 'var(--gw-red, #d66b60)' }} />
            : renderUnreadCount(dot, 12, countKey))}
        </button>
        {showFav && renderFavStar(label, fav, { right: showSplit ? '36px' : '6px', size: 26, icon: 15 })}
        {showSplit && (
          <button
            type="button"
            className="gw-sidebar-split-open"
            disabled={splitAction.disabled}
            title={splitAction.title}
            aria-label={splitLabel || `${label} 보조 패널로 열기`}
            onClick={(e) => { e.stopPropagation(); if (!splitAction.disabled) splitAction.onClick(); }}
            style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', width: '26px', height: '26px', border: 'none', borderRadius: '6px', background: 'var(--gw-surface, #fff)', color: 'var(--gw-secondary, #64635f)', cursor: splitAction.disabled ? 'not-allowed' : 'pointer', boxShadow: '0 1px 4px rgba(15,23,42,.16)', fontSize: '15px', lineHeight: 1 }}
          >
            <IconColumns2 size={15} stroke={1.8} />
          </button>
        )}
      </div>
    );
  };
  const renderSubItems = (it) => {
    if (!it.subItems || app !== it.key || collapsed) return null;
    const visibleSubItems = it.subItems.filter(canSee);
    if (!visibleSubItems.length) return null;
    return visibleSubItems.map((sub) => {
      const Icon = iconFor(sub.key);
      const active = location.pathname === sub.path;
      return (
        <button key={sub.key} type="button" className="gw-sidebar-btn" onClick={() => go(sub.path)} aria-current={active ? 'page' : undefined}
          onMouseEnter={active ? undefined : (e) => { e.currentTarget.style.background = 'var(--gw-hover, #efefed)'; }}
          onMouseLeave={active ? undefined : (e) => { e.currentTarget.style.background = 'transparent'; }}
          style={{ width: '100%', minHeight: '28px', display: 'flex', alignItems: 'center', gap: '7px', padding: '7px 12px 7px 34px', marginBottom: '2px', borderRadius: '5px', border: 'none', cursor: 'pointer', fontWeight: active ? 550 : 400, fontSize: `${NAV_FONT_PX}px`, background: active ? 'var(--gw-selected, #eaeae7)' : 'transparent', color: active ? 'var(--gw-text, #32312e)' : 'var(--gw-secondary, #64635f)', textAlign: 'left', boxSizing: 'border-box', WebkitTapHighlightColor: 'transparent' }}>
          {renderMenuIcon(Icon, toneFor(sub.key), 16)}
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub.label}</span>
        </button>
      );
    });
  };

  const tokenPct = myUsage.limit > 0 ? Math.round(((myUsage.used || 0) / myUsage.limit) * 100) : null;
  const notificationBadge = notificationUnread > 99 ? '99+' : String(notificationUnread);
  // 알림 단추 — mode: true = 접힘 아이콘 단추(40×38) · 'head' = 사이드바 머리 2행 아이콘(28×28). (전폭 줄 모드는 모바일 드로어 바닥과 함께 없어졌다 · f8b2)
  //   두 모드 모두 알림 센터 창이 단추 «아래»로 열린다(NotificationCenter placement="below" — 창이 스스로 top 기준·남는 높이·셸 폭에 맞춘다 · f8b2).
  //   감싸개(gw-notif-below)의 `[role=dialog]{top:0!important}` 규칙은 같은 결과를 CSS 로도 보장하는 이중 장치로 남긴다.
  //   'head' 는 창 기준점이 «알림 단추»가 아니라 «머리 2행»(position:relative)이다 — 바깥 div 를 static 으로 두어 감싸개가 2행 기준 left:12px 에 서고,
  //   창(NotificationCenter left:0 · 폭 min(360px, 100vw-24px))이 사이드바 왼쪽 끝에서 시작한다(단추 기준이면 사이드바 오른쪽 밖으로 넘어가 분할 보기·임베드 슬롯에서 잘렸다).
  const renderNotificationButton = (mode = false) => {
    const iconOnly = mode === true;
    const head = mode === 'head';
    // 서랍 모드의 머리 알림 창(f8b · 모바일 포함 f8b2): 서랍 패널(폭 238·overflow auto·transform)에 잘리지 않게 body 포털 + position:fixed 로 단추 아래에 그린다.
    //   데스크톱(서랍 아님) 경로는 아래 absolute 감싸개 그대로. 감싸개 className 은 같아 aside <style> 의 `.gw-notif-below>[role=dialog]` 규칙(문서 전역)이 포털에도 먹는다.
    //   포털 창은 aria-modal 서랍 «밖» body 끝에 놓이므로 modal(초점 이동·Tab 가둠·aria-modal·닫으면 초점 복귀)로 그린다 — 그렇지 않으면 서랍이 모달인 동안 낭독기·Tab 이 창에 닿지 못한다.
    const portalHead = head && drawerMode;
    const center = <NotificationCenter open={notificationOpen} compact={iconOnly} placement="below" modal={portalHead} onClose={() => setNotificationOpen(false)} onUnreadChange={setNotificationUnread} />;
    return (
      <div style={{ position: head ? 'static' : 'relative', width: head ? '28px' : '40px', alignSelf: 'center', flexShrink: 0 }}>
        <button
          type="button"
          ref={head ? notifBtnRef : undefined}
          onClick={() => {
            // rect 는 화면 px · fixed top 은 CSS px — 웹 배율(<html> zoom)이 걸리면 단추 높이로 배율을 재서 나눈다(1.0·jsdom 은 1).
            if (portalHead && !notificationOpen && notifBtnRef.current) {
              const btn = notifBtnRef.current;
              const r = btn.getBoundingClientRect();
              const s = scaleOf(r.height, btn.offsetHeight);
              setNotifTop(Math.round(r.bottom / s) + 4);
            }
            setNotificationOpen((v) => !v);
          }}
          title="알림"
          aria-label="알림"
          aria-haspopup="dialog"
          aria-expanded={notificationOpen}
          {...(head ? headHover(notificationOpen) : {})}
          style={head
            ? { ...headIconBtn(notificationOpen), position: 'relative' }
            : { ...iconFootBtn(notificationOpen), position: 'relative' }}
        >
          <IconBell size={head ? 17 : 19} stroke={1.8} style={{ flexShrink: 0 }} />
          {notificationUnread > 0 && (
            <span
              aria-label={`미읽음 ${notificationUnread}개`}
              style={head
                ? { position: 'absolute', top: '-2px', right: '-4px', minWidth: '16px', height: '16px', padding: '0 4px', borderRadius: '999px', background: '#ef4444', color: '#fff', fontSize: '10px', fontWeight: 800, lineHeight: '16px', boxSizing: 'border-box' }
                : { position: 'absolute', top: '3px', right: '2px', minWidth: '16px', height: '16px', padding: '0 4px', borderRadius: '999px', background: '#ef4444', color: '#fff', fontSize: '10px', fontWeight: 800, lineHeight: '16px', boxSizing: 'border-box' }}
            >
              {notificationBadge}
            </span>
          )}
        </button>
        {portalHead
          ? createPortal(<div className="gw-notif-below" style={{ position: 'fixed', top: `${notifTop}px`, left: '12px', width: 0, height: 0, zIndex: 1102 }}>{center}</div>, document.body)
          : head
          ? <div className="gw-notif-below" style={{ position: 'absolute', top: 'calc(100% + 4px)', left: '12px', width: 0, height: 0 }}>{center}</div>
          : <div className="gw-notif-below" style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, width: 0, height: 0 }}>{center}</div>}
      </div>
    );
  };
  // 검색 단추 — mode: true = 접힘 아이콘 단추(40×38) · 'head' = 머리 2행 아이콘(28×28). (전폭 줄 모드는 모바일 드로어 바닥과 함께 없어졌다 · f8b2)
  const renderSearchButton = (mode) => {
    const head = mode === 'head';
    return (
      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        title="검색"
        aria-label="검색"
        {...(head ? headHover(false) : {})}
        style={head ? headIconBtn(false) : iconFootBtn(false)}
      >
        <IconSearch size={head ? 17 : 19} stroke={1.8} style={{ flexShrink: 0 }} />
      </button>
    );
  };
  // 머리 2행 아이콘 단추(검색·AI 채팅·캘린더·알림 공용) — 28×28 투명 r6. 마우스를 올리면 var(--gw-hover) 바탕.
  const renderHeadIconButton = (label, Icon, onClick) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      style={headIconBtn(false)}
      {...headHover(false)}
    >
      <Icon size={17} stroke={1.8} style={{ flexShrink: 0 }} />
    </button>
  );
  // «이번 달 사용량» 블록 — 상단 바 계정 창으로 옮겨졌다(f8a2 · 예전 사이드바 바닥). 토큰·웹 검색 한도가 하나도 없으면 아무것도 안 그린다.
  const renderUsage = () => {
    if (!(myUsage.limit > 0 || myUsage.web_search_limit > 0)) return null;
    return (
      <div style={{ marginBottom: '10px' }}>
        <div onClick={() => setUsageOpen((o) => { const n = !o; publishSidebarPref('usageOpen', n); return n; })} title={usageOpen ? '접기' : '펼치기'}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontSize: '13px', color: 'var(--gw-text, #32312e)', fontWeight: 600, marginBottom: usageOpen ? '6px' : 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><IconChartBar size={16} stroke={1.8} />이번 달 사용량</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {!usageOpen && tokenPct != null && <span style={{ color: tokenPct >= 100 ? '#ef4444' : tokenPct >= 80 ? '#f59e0b' : COLORS.text, fontWeight: 700, fontSize: '13px' }}>{tokenPct}%</span>}
            <span>{usageOpen ? '▾' : '▸'}</span>
          </span>
        </div>
        {usageOpen && myUsage.limit > 0 && (() => {
          const limit = myUsage.limit, used = myUsage.used || 0;
          const myUsed = myUsage.my_used != null ? myUsage.my_used : used;
          const groupUsed = myUsage.group_used || 0, isShared = myUsage.scope === 'shared';
          const pct = Math.round((used / limit) * 100), groupPct = Math.round((groupUsed / limit) * 100);
          const myPct = Math.max(pct - groupPct, 0);
          const barColor = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : COLORS.primary;
          const pctTextColor = pct >= 80 ? barColor : COLORS.text;
          const myW = Math.min((myUsed / limit) * 100, 100), grW = Math.min((groupUsed / limit) * 100, Math.max(0, 100 - myW));
          return (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '12px', color: 'var(--gw-muted, #92918d)', marginBottom: '5px' }}>
                <span>토큰{isShared ? <span style={{ color: 'var(--gw-secondary, #64635f)' }}> · 그룹 공유</span> : ''}</span>
                <span style={{ color: pctTextColor, fontWeight: '700', fontSize: '13px' }}>{pct}%</span>
              </div>
              <div style={{ height: '5px', background: 'var(--gw-line, #eeeeec)', borderRadius: '3px', display: 'flex', overflow: 'hidden' }}>
                {isShared
                  ? (<><div style={{ width: `${myW}%`, height: '100%', background: COLORS.primary, transition: 'width .3s' }} /><div style={{ width: `${grW}%`, height: '100%', background: '#fbbf24', transition: 'width .3s' }} /></>)
                  : (<div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: barColor, borderRadius: '3px', transition: 'width .3s' }} />)}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--gw-muted, #92918d)', marginTop: '4px', textAlign: 'right' }}>{used.toLocaleString()} / {limit.toLocaleString()}</div>
              {isShared && (
                <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '11px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: COLORS.text, fontWeight: '600' }}><span><span style={{ display: 'inline-block', width: '7px', height: '7px', background: 'var(--gw-secondary, #64635f)', borderRadius: '2px', marginRight: '4px' }} />나</span><span>{myUsed.toLocaleString()} · {myPct}%</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#fbbf24', fontWeight: '600' }}><span><span style={{ display: 'inline-block', width: '7px', height: '7px', background: '#fbbf24', borderRadius: '2px', marginRight: '4px' }} />그룹원</span><span>{groupUsed.toLocaleString()} · {groupPct}%</span></div>
                </div>
              )}
            </div>
          );
        })()}
        {usageOpen && myUsage.web_search_limit > 0 && (() => {
          const lim = myUsage.web_search_limit, used = myUsage.web_search_used || 0;
          const isShared = myUsage.web_search_scope === 'shared';
          const pct = Math.round((used / lim) * 100);
          const c = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : COLORS.primary;
          const pctTextColor = pct >= 80 ? c : COLORS.text;
          return (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '12px', color: 'var(--gw-muted, #92918d)', marginBottom: '5px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><IconWorld size={15} stroke={1.8} />웹 검색{isShared ? <span style={{ color: 'var(--gw-secondary, #64635f)' }}> · 그룹 공유</span> : ''}</span>
                <span style={{ color: pctTextColor, fontWeight: '700', fontSize: '13px' }}>{pct}%</span>
              </div>
              <div style={{ height: '5px', background: 'var(--gw-line, #eeeeec)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: c, borderRadius: '3px', transition: 'width .3s' }} />
              </div>
              <div style={{ fontSize: '11px', color: 'var(--gw-muted, #92918d)', marginTop: '4px', textAlign: 'right' }}>{used.toLocaleString()} / {lim.toLocaleString()}회</div>
            </div>
          );
        })()}
      </div>
    );
  };
  // 사이드바 바닥 «OURi와 새 대화» — 채팅이 아닌 화면은 '/' 로 이동 · 채팅 화면은 '/' 로 옮긴 뒤 popstate 를 한 번 쏜다.
  //   ChatPage 의 popstate 처리가 `?conv` 없는 주소를 «새 대화»(startNew)로 읽는다 — 그 처리를 그대로 재사용한다(ChatPage 는 고치지 않는다).
  //   🔴 BrowserRouter 라 navigate 가 pushState 를 동기로 부른다 → 곧바로 쏴도 handler 가 이미 '/' 를 본다.
  const startOuriChat = () => {
    navigate('/');
    if (app === 'chat') window.dispatchEvent(new PopStateEvent('popstate'));
  };
  // 상단 바 내용(AppShell 상단 바에 포털 · 데스크톱만) — 현재 메뉴는 데스크톱 nav 의 «현재»(cur = app === it.key)와 같은 식.
  //   메뉴에 없는 키면 «아워박스 워크스페이스» 만 — 이름을 따로 만들어 붙이지 않는다(메뉴 설정 resolveNav 가 이름의 정본).
  //   오른쪽 .gw-top-actions = 화면 이름 안내(시안 F .target-label 자리 · 마스터 10-22) — f8a2 가 그 뒤에 별·아바타를 더한다.
  const curItem = navItems.find((it) => !it.section && !it.divider && canSee(it) && it.key === app) || null;
  const renderTopbar = () => {
    const CurIcon = curItem ? iconFor(curItem.key) : null;
    // 좁은 폭 서랍(≤900 · f8b): «아워박스 워크스페이스» 와 그 뒤 «/» 는 감춘다(시안 F .crumb-parent{display:none}) — 현재 메뉴가 없는 화면이면 빈 브레드크럼이 되니 남긴다.
    const showParent = !(drawerMode && curItem);
    const breadcrumb = (
      <nav aria-label="현재 위치" className="gw-breadcrumb" style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden' }}>
        {showParent && <span style={{ color: 'var(--gw-muted, #92918d)' }}>아워박스 워크스페이스</span>}
        {curItem && showParent && <span aria-hidden="true" style={{ color: 'var(--gw-border, #e5e5e2)' }}>/</span>}
        {curItem && (
          <span aria-current="page" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--gw-text, #32312e)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {CurIcon ? <CurIcon size={15} stroke={1.65} aria-hidden="true" /> : null}
            {curItem.label}
          </span>
        )}
      </nav>
    );
    return (
      <>
        {drawerMode ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <button ref={menuOpenBtnRef} type="button" className="gw-top-menu" aria-label="메뉴 열기" aria-haspopup="dialog" aria-expanded={drawerOpen} onClick={openDrawer}
              style={{ ...headIconBtn(false), color: 'var(--gw-text, #32312e)' }} {...headHover(false)}>
              <IconMenu2 size={18} stroke={1.8} />
            </button>
            {breadcrumb}
          </div>
        ) : breadcrumb}
        <div className="gw-top-actions" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '7px', flexShrink: 0 }}>
          {curItem && (
            <span className="gw-topbar-label" title="현재 화면" style={{ fontSize: '10px', color: 'var(--gw-muted, #92918d)', background: 'var(--gw-subtle, #fbfbfa)', padding: '5px 8px', border: '1px solid var(--gw-line, #eeeeec)', borderRadius: '4px', whiteSpace: 'nowrap' }}>{curItem.label}</span>
          )}
          {/* 현재 화면 즐겨찾기 별(f8a2) — 사이드바 «편집» 별과 같은 onToggleFavorite. 🔴 이름에 메뉴 이름을 넣지 않는다(편집 별 `${label} 즐겨찾기 추가` 와 겹친다). */}
          {curItem && (() => {
            const on = favKeys.includes(curItem.key);
            const disabled = !on && favKeys.length >= MAX_FAVORITES;
            const name = `현재 화면 즐겨찾기 ${on ? '해제' : '추가'}`;
            return (
              <button type="button" className="gw-top-fav" disabled={disabled} aria-label={name} title={disabled ? `즐겨찾기는 ${MAX_FAVORITES}개까지` : name}
                onClick={() => { if (!disabled) onToggleFavorite(curItem.key); }}
                style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 'none', borderRadius: '6px', background: 'transparent', color: on ? '#fbbf24' : 'var(--gw-muted, #92918d)', opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}>
                <IconStar size={17} stroke={1.8} fill={on ? 'currentColor' : 'none'} />
              </button>
            );
          })()}
          {/* 내 계정 창(f8a2) — 아바타 단추 + 창을 acctRef 한 요소로 감싼다(바깥 mousedown 판정). 창은 .gw-top-actions(relative) 기준 오른쪽 정렬. */}
          <span ref={acctRef} style={{ display: 'inline-flex' }}>
            <button ref={avatarBtnRef} type="button" aria-label="내 계정 메뉴" aria-haspopup="dialog" aria-expanded={acctOpen} onClick={toggleAcct}
              style={{ width: '25px', height: '25px', borderRadius: '50%', border: 'none', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gw-gray-soft, #f1f1ef)', color: 'var(--gw-text, #32312e)', fontSize: '10px', fontWeight: 600, cursor: 'pointer', flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}>
              {Array.from(String(user.name || user.email || '?'))[0]}
            </button>
            {acctOpen && (
              <div role="dialog" aria-label="내 계정"
                style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: '260px', maxHeight: 'calc(min(var(--gw-app-h, 100dvh), var(--gw-vv-h, var(--gw-app-h, 100dvh))) - var(--gw-topbar-h, 48px) - 16px)', overflowY: 'auto', zIndex: 1000, boxSizing: 'border-box', padding: '14px', background: 'var(--gw-surface, #fff)', border: '1px solid var(--gw-line, #eeeeec)', borderRadius: '8px', boxShadow: '0 12px 32px rgba(15,23,42,.18)' }}>
                <div style={{ marginBottom: '12px', minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gw-text, #32312e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name || user.email || ''}</div>
                  {user.name && user.email && (
                    <div style={{ fontSize: '11px', color: 'var(--gw-muted, #92918d)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                  )}
                </div>
                {renderUsage()}
                {/* 편집 중에는 사이드바 바닥의 «즐겨찾기 편집 완료» 가 끝낸다 — 같은 이름 단추가 두 개 서지 않게 여기선 «시작»만 둔다.
                    접힘이면 ☆ 가 안 그려져 편집이 무의미하다(예전 바닥도 접힘엔 편집 단추가 없었다) → 접힘이면 뺀다. */}
                {!favEdit && !collapsed && <div style={{ marginBottom: '10px' }}>{renderFavEditButton(() => { setAcctOpen(false); if (drawerMode) setDrawerOpen(true); })}</div>}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button type="button" onClick={logout} style={footBtn}>로그아웃</button>
                </div>
              </div>
            )}
          </span>
        </div>
      </>
    );
  };

  // 머리 접기 단추(f8a2: 펼침=1행 오른쪽 · 접힘=가운데) — 펼침 «, 접힘 »
  //   좁은 폭 서랍(drawerMode)이면 접기 대신 서랍 닫기 «메뉴 닫기» (접기 설정은 건드리지 않는다).
  const collapseLabel = drawerMode ? '메뉴 닫기' : (collapsed ? '사이드바 펼치기' : '사이드바 접기');
  const collapseButton = (
    <button ref={drawerMode ? menuCloseBtnRef : undefined} type="button" onClick={drawerMode ? () => setDrawerOpen(false) : toggleCollapse} title={collapseLabel} aria-label={collapseLabel}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', flexShrink: 0, borderRadius: '7px', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--gw-secondary, #64635f)' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gw-line, #eeeeec)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
      {collapsed ? <IconChevronRight size={18} stroke={2} /> : <IconChevronLeft size={18} stroke={2} />}
    </button>
  );
  // 머리 2행의 AI 채팅·캘린더 아이콘 — 메뉴 설정을 적용한 navItems 에 그 메뉴가 있고 canSee 일 때만(숨긴 메뉴·권한 없는 메뉴면 아이콘 자체가 없다).
  const headNavItem = (key) => navItems.find((it) => !it.section && !it.divider && it.key === key && canSee(it)) || null;
  const headNavVisible = (key) => headNavItem(key) !== null;
  // 머리 홈 알약 — 글자·접근 이름은 메뉴 정본 이름(navItems 의 key 'home' label · 기본 «메인»). 그 메뉴가 없거나 안 보이면 알약을 그리지 않는다.
  const homeItem = headNavItem('home');

  // aside — 서랍 모드(좁은 폭·모바일)면 MobileDrawer 패널 안에 넣는다(패널이 폭을 정하니 aside 는 100%). 메뉴 목록·머리·바닥은 이 한 벌이다(f8b2 — 모바일 전용으로 따로 그린 목록·바닥 단추는 없앴다).
  const asideW = drawerMode ? '100%' : (collapsed ? COLLAPSED_W : sidebarWidth);
  const asideEl = (
    <aside aria-label="앱 메뉴" style={{ width: asideW, minWidth: asideW, background: 'var(--gw-sidebar, #f7f7f5)', color: 'var(--gw-secondary, #64635f)', borderRight: drawerMode ? 'none' : '1px solid var(--gw-line, #eeeeec)', display: 'flex', flexDirection: 'column', height: drawerMode ? 'var(--gw-vv-h, var(--gw-app-h, 100dvh))' : 'var(--gw-app-h, 100dvh)', position: 'relative' }}>
      <style>{`.sb-scroll{scrollbar-width:thin;scrollbar-color:rgba(0,0,0,.2) transparent}.sb-scroll::-webkit-scrollbar{width:6px}.sb-scroll::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:3px}.gw-sidebar-btn:focus-visible,.gw-sidebar-split-open:focus-visible,.gw-sidebar-fav:focus-visible{outline:2px solid var(--gw-blue, #2383e2);outline-offset:2px}.gw-sidebar-split-open{opacity:0;transition:opacity .12s ease}.gw-sidebar-navrow:hover .gw-sidebar-split-open,.gw-sidebar-navrow:focus-within .gw-sidebar-split-open{opacity:1}.gw-sidebar-split-open:disabled{color:#cbd5e1}.gw-notif-below>[role=dialog]{top:0!important;bottom:auto!important}@media (min-width:901px) and (max-width:1200px){.gw-wordmark{font-size:14px!important}}`}</style>
      {!collapsed && !drawerMode && (
        <div onMouseDown={startResize} title="드래그하여 너비 조절"
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '5px', cursor: 'col-resize', zIndex: 20 }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(100,116,139,.28)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} />
      )}

      {/* 머리(f8a2) — 펼침: 1행 워드마크 + 접기 단추 · 2행 홈 알약 + 아이콘 단추(검색·AI 채팅·캘린더·알림).
          접힘: 접기 단추 아래 세로로 검색·알림 아이콘만(나머지는 펼쳐야 보인다). */}
      {collapsed ? (
        <div style={{ flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 2px' }}>
            {collapseButton}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '4px 0 8px' }}>
            {renderSearchButton(true)}
            {renderNotificationButton(true)}
          </div>
        </div>
      ) : (
        <div style={{ flexShrink: 0 }}>
          <div style={{ height: '59px', boxSizing: 'border-box', padding: '20px 16px 11px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <span className="gw-wordmark" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '15px', fontWeight: 660, letterSpacing: '-0.7px', color: 'var(--gw-text, #32312e)' }}>{SERVICE_NAME}</span>
            {collapseButton}
          </div>
          <div data-gw-head-row="2" style={{ position: 'relative', height: '43px', boxSizing: 'border-box', padding: '0 12px 12px', display: 'flex', alignItems: 'center', gap: '2px' }}>
            {homeItem && <button type="button" aria-label={homeItem.label} onClick={() => go('/home')}
              style={{ height: '29px', boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '0 11px 0 9px', marginRight: 'auto', borderRadius: '20px', border: '1px solid var(--gw-line, #eeeeec)', background: 'var(--gw-surface, #fff)', color: 'var(--gw-text, #32312e)', fontSize: '12px', fontWeight: 500, cursor: 'pointer', flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}>
              <IconHome size={15} stroke={1.8} style={{ flexShrink: 0 }} />{homeItem.label}
            </button>}
            {!homeItem && <span aria-hidden="true" style={{ flex: 1 }} />}
            {renderSearchButton('head')}
            {headNavVisible('chat') && renderHeadIconButton('AI 채팅 열기', IconMessageCircle, () => go('/'))}
            {headNavVisible('calendar') && renderHeadIconButton('캘린더 열기', IconCalendar, () => go('/calendar'))}
            {renderNotificationButton('head')}
          </div>
        </div>
      )}

      {/* nav + 현재 앱 콘텐츠(아코디언) — 한 스크롤 */}
      <div ref={setNavScrollRef} onScroll={saveNavScrollTop} className="sb-scroll" role="navigation" aria-label="주요 메뉴" style={{ flex: 1, minHeight: drawerMode ? '120px' : 0, overflowY: 'auto', overscrollBehavior: 'contain', padding: '12px 8px' }}>
        {/* 메뉴를 그리는 곳은 이 aside 하나다(f8b2 — 예전에는 모바일 드로어가 따로 한 벌 더 그렸다: 2026-08-18 센터 메뉴 때 한쪽만 고쳐 그쪽에서만 메뉴가 보였다).
            모바일 서랍도 이 aside 를 MobileDrawer 에 담을 뿐이다. */}
        {/* «내 즐겨찾기» 묶음 — 있을 때만(편집 상태와 무관). 묶음 줄은 aria-current·☆/★·하위 메뉴 없음 · 접힘이면 머리 없이 아이콘만.
            보조 패널 단추는 있다(10-20 마스터) — 이름을 «<메뉴>(즐겨찾기) 보조 패널로 열기» 로 달리해 e2e 의 «<메뉴> 보조 패널로 열기» 조회(완전·부분 일치 모두)와 겹치지 않게 한다. 접두로 바꾸면 부분 일치에 걸린다.
            머리글 = 접기 단추(sidebarFavoritesOpen) · 아이콘만 모드는 머리글이 없으니 접기 무시(늘 보인다). */}
        {favItems.length > 0 && [
          collapsed ? null : renderFavHeader(),
          ...((collapsed || favOpen) ? favItems.map((it) => (
            <Fragment key={'fav-' + it.key}>{navBtn(false, it.label, () => go(it.path), navCount(it), iconFor(it.key),
              getSplitAction(it), null, `${it.label}(즐겨찾기) 보조 패널로 열기`, toneFor(it.key), it.key)}</Fragment>
          )) : []),
          <div key="favorites-divider" style={{ height: '1px', background: 'var(--gw-line, #eeeeec)', margin: '10px 8px' }} />,
        ]}
        {navItems.map((it, i) => {
          if (it.section) {
            // 그 섹션에 보이는 항목이 하나라도 있을 때만 헤더 렌더(다음 섹션 전까지 탐색)
            let hasVisible = false;
            for (let j = i + 1; j < navItems.length && !navItems[j].section; j++) {
              if (!navItems[j].divider && (canSee(navItems[j]) || canRequestAccess(navItems[j]))) { hasVisible = true; break; }
            }
            if (!hasVisible) return null;
            if (collapsed) return <div key={'sec-' + it.section} style={{ height: '1px', background: 'var(--gw-line, #eeeeec)', margin: '8px 10px' }} />;
            // 그룹 머리글 = 접기 단추 — 구분선 없이 여백으로만 나눈다(시안 F). 접혔으면 숨은 줄(현재 앱 줄 제외)의 안읽음을 센다.
            const open = !folded(it.section);
            let hiddenUnread = 0;
            if (!open) {
              for (let j = i + 1; j < navItems.length && !navItems[j].section; j++) {
                const x = navItems[j];
                if (!x.divider && canSee(x) && x.key !== app) hiddenUnread += navCount(x);
              }
            }
            return renderGroupHeader(it.section, open, hiddenUnread);
          }
          if (it.divider) return folded(groupAt[i]) ? null : <div key={it.key} style={{ height: '1px', background: 'var(--gw-line, #eeeeec)', margin: '10px 8px' }} />;
          if (!canSee(it)) {
            if (!canRequestAccess(it) || folded(groupAt[i])) return null;
            // 권한 요청 줄 = tone 을 넘기지 않는다(흐린 회색 · 모바일 권한 요청 줄과 같게).
            return <Fragment key={it.key}>{navBtn(false, `${it.label} 권한 요청`, () => go('/inquiries'), false, iconFor(it.key))}</Fragment>;
          }
          const cur = app === it.key;
          // 현재 앱 줄은 그룹이 접혀도 남긴다(하위 메뉴·children = 채팅 대화 목록 같은 페이지 사이드 콘텐츠가 사라지지 않게).
          if (!cur && folded(groupAt[i])) return null;
          const dot = navCount(it);
          return (
            <Fragment key={it.key}>
              {navBtn(cur, it.label, () => go(it.path), dot, iconFor(it.key), getSplitAction(it), favFor(it), null, toneFor(it.key), it.key)}
              {cur ? renderSubItems(it) : null}
              {!collapsed && cur && children ? <div>{children}</div> : null}
            </Fragment>
          );
        })}
      </div>

      {/* 하단(고정, f8a2): «OURi와 새 대화» 알약 + 그 아래 보기 줄(«PC | 모바일» 전환 + 글자 크기 −/+ · PC·모바일 공통 · 계정 창엔 없음). 사용량·계정·로그아웃·검색·알림·즐겨찾기 편집(시작)은 위(상단 바 계정 창·머리)로 옮겨졌다.
          즐겨찾기 편집 중에만 «즐겨찾기 편집 완료» 가 알약 위에 선다. 접힘이면 아이콘 단추 하나(보기 줄 없음 · 펼치면 보인다). 769~900 서랍·모바일 서랍은 늘 펼친 모양이라 줄이 있다. */}
      <div style={{ padding: collapsed ? '10px 0' : '12px 14px 10px', borderTop: '1px solid var(--gw-line, #eeeeec)', flexShrink: 0 }}>
        {collapsed ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            {headNavVisible('chat') && <button type="button" onClick={startOuriChat} title="OURi와 새 대화" aria-label="OURi와 새 대화" style={iconFootBtn(false)}><IconSparkles size={19} stroke={1.8} /></button>}
          </div>
        ) : (<>
        {favEdit && <div style={{ marginBottom: '10px' }}>{renderFavEditButton()}</div>}
        {headNavVisible('chat') && <button type="button" onClick={startOuriChat}
          style={{ width: '100%', height: '36px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '0 12px', borderRadius: '22px', border: '1px solid var(--gw-line, #eeeeec)', background: 'var(--gw-surface, #fff)', boxShadow: '0 2px 6px rgba(15,23,42,.08)', color: 'var(--gw-text, #32312e)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
          <IconSparkles size={16} stroke={1.8} style={{ flexShrink: 0 }} />OURi와 새 대화
        </button>}
        <SidebarViewControls />
        {/* 모바일 임베드(상단 바 없음 → «내 계정» 창도 없다): 로그아웃만 서랍 바닥에 둔다(보기 줄은 바로 위에 이미 있다 — 두 벌 금지). 상단 바가 있는 화면엔 두지 않는다. */}
        {mobile && !topbarEl && (
          <div data-gw-drawer-account="1">
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button type="button" onClick={logout} style={footBtn}>로그아웃</button>
            </div>
          </div>
        )}
        </>)}
      </div>
    </aside>
  );
  // aside 밖 전역 조각 — 서랍 패널은 닫히면 visibility:hidden + transform 이라 position:fixed 자식의 기준이 패널이 된다(검색 창이 안 보이거나 패널 안에 갇힘).
  //   GlobalSearch·GlobalAnnouncementPopup 은 닫힌 동안 null, 열리면 position:fixed 만 그린다(흐름 폭 0) · 상단 바 포털은 topbarEl 로 가니 위치와 무관.
  // 전역 창(공지 팝업·통합 검색) — 서랍이 body 로 나가 슬롯(z 25) 안 창이 서랍 밑에 깔린다 → 서랍이 «열린 동안만» body 로 그린다(숨은 슬롯 인스턴스는 늘 닫혀 있어 제자리 display:none 그대로 · Ctrl+K·공지가 겹쳐 보이지 않는다).
  const globalOverlays = (
    <>
      <GlobalAnnouncementPopup />
      <GlobalSearch open={searchOpen} onOpen={() => setSearchOpen(true)} onClose={() => setSearchOpen(false)} />
    </>
  );
  const globalOverlaysEl = drawerOpen && typeof document !== 'undefined' ? createPortal(globalOverlays, document.body) : globalOverlays;
  const globalsEl = (
    <>
      {globalOverlaysEl}
      {topbarEl ? createPortal(renderTopbar(), topbarEl) : null}
    </>
  );
  if (drawerMode) {
    return (
      <>
        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} width={DRAWER_W}>{asideEl}</MobileDrawer>
        {/* 하단 탭바는 모바일만(마스터 결정 f8b2 — 769~900 서랍은 탭바 없이 상단 바 «메뉴 열기» 뿐). 탭바 «메뉴» 도 같은 서랍을 연다. */}
        {mobile && <span ref={tabAnchorRef} hidden aria-hidden="true" style={{ display: 'none' }} />}
        {mobile && <MobileTabBar onMenuClick={openDrawer} />}
        {globalsEl}
      </>
    );
  }
  return (<>{asideEl}{globalsEl}</>);
}

const DRAWER_W = 238;   // 서랍 폭(모바일·좁은 폭 서랍 공통 · 시안 F)
const footBtn = { flex: 1, minHeight: '38px', padding: '8px', background: 'var(--gw-gray-soft, #f1f1ef)', color: 'var(--gw-text, #32312e)', border: '1px solid var(--gw-line, #eeeeec)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, boxSizing: 'border-box', WebkitTapHighlightColor: 'transparent' };
// 접힘 상태 아이콘 버튼(검색·알림·OURi 새 대화)
const iconFootBtn = (active) => ({ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '38px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: active ? COLORS.primarySoft : 'transparent', color: active ? COLORS.selectedText : 'var(--gw-secondary, #64635f)', WebkitTapHighlightColor: 'transparent' });
// 머리 2행 아이콘 단추 마우스 올림(검색·AI 채팅·캘린더·알림 공용 · f8b) — 올리면 var(--gw-hover), 내리면 그 단추의 원래 바탕(켜짐이면 primarySoft)으로.
const headHover = (active) => ({
  onMouseEnter: (e) => { if (!active) e.currentTarget.style.background = 'var(--gw-hover, #efefed)'; },
  onMouseLeave: (e) => { e.currentTarget.style.background = headIconBtn(active).background; },
});
// 머리 2행 아이콘 단추(검색·AI 채팅·캘린더·알림) — 28×28 투명 r6
const headIconBtn = (active) => ({ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', padding: 0, flexShrink: 0, borderRadius: '6px', border: 'none', cursor: 'pointer', background: active ? COLORS.primarySoft : 'transparent', color: active ? COLORS.selectedText : 'var(--gw-secondary, #64635f)', WebkitTapHighlightColor: 'transparent' });
